import type { UseDashboardType } from '@/state/useDashboard';
import { EmptyState, MetricGrid, MetricTile, Panel, PillTabs, TrendChip } from '../ConsolePanel';
import { buildWindowMetrics, conversionMetricLabel } from './dashboard-metrics';

type VisitorsProps = {
  dashboardManager: UseDashboardType;
  conversionEvent: string | null | undefined;
};

export function Visitors({ dashboardManager, conversionEvent }: VisitorsProps) {
  const { dashboardStats, isLoading, timespan, setTimespan, timespanOptions } = dashboardManager;

  if (isLoading) return null;

  // Kept on screen rather than dropped, so the page does not change shape the moment
  // the first stats arrive.
  if (!dashboardStats) {
    return (
      <Panel title="Visitors" description="Sessions, conversions and value" tourAnchor="home-visitors">
        <EmptyState>Visitors appear here once an experiment is running and recording sessions.</EmptyState>
      </Panel>
    );
  }

  const currentLabel = timespanOptions.find((opt) => opt.value === timespan)?.label ?? 'All time';
  const metrics = buildWindowMetrics(dashboardStats, conversionMetricLabel(conversionEvent));

  return (
    <Panel
      title="Visitors"
      description={currentLabel}
      tourAnchor="home-visitors"
      // The timespan used to float over the card's top-right corner. It belongs
      // to the panel, so it sits in the panel's head with everything else that
      // re-scopes what is under it.
      actions={<PillTabs options={timespanOptions} value={timespan} onChange={setTimespan} />}
    >
      <MetricGrid>
        {metrics.map((metric) => (
          <MetricTile
            key={metric.title}
            label={metric.title}
            value={metric.value}
            description={metric.description}
            subtitle={metric.subtitle}
            trailing={metric.trend && <TrendChip trend={metric.trend} />}
          />
        ))}
      </MetricGrid>
    </Panel>
  );
}
